import { Link } from 'react-router-dom';

import { Icon } from '@/components/ui/icon';
import { Text } from '@/components/ui/text';
import type { MenuItem } from '@/config/navigation';

interface AppMenuGridProps {
  items: MenuItem[];
  /** Repassa a decisão de histórico do `AppMenu` (`isDestinationRoute`). */
  replace: boolean;
  onSelect: () => void;
}

/**
 * O menu em **grade de cards** — em teste atrás de `MENU_GRID_ENABLED`.
 *
 * ## Por que grade
 *
 * São sete portas, nove para quem administra a plataforma. Em linhas, isso é
 * uma coluna que passa da metade da tela e obriga a rolar dentro de um painel
 * que já é uma sobreposição; a pessoa lê de cima para baixo até achar. Em
 * grade, as mesmas portas cabem numa varredura só, e a escolha vira
 * reconhecimento de ícone em vez de leitura.
 *
 * `grid-cols-3` sem breakpoint: o alvo é o telefone (docs/07). Em 360px cada
 * célula fica com ~105px — bem acima dos 44px de alvo — e rótulo de duas
 * palavras cabe quebrando em duas linhas, que é por que o card tem altura
 * mínima e centraliza o conteúdo em vez de ancorar no topo.
 *
 * ## O que se perde, de propósito
 *
 * A descrição do item não aparece aqui: três colunas não comportam uma linha de
 * apoio legível, e encolher a fonte para caber daria um texto que ninguém lê
 * mesmo assim. O rótulo passa a carregar tudo — a troca está anotada na
 * bandeira, em `config/features.ts`.
 *
 * Sem borda e com fundo `surface-subtle`: o card sobe do branco do painel por
 * preenchimento, não por moldura. Nove molduras lado a lado viram grade de
 * linhas desenhadas, e o que precisa saltar é o ícone.
 */
export function AppMenuGrid({ items, replace, onSelect }: AppMenuGridProps) {
  return (
    <ul className="grid grid-cols-3 gap-2">
      {items.map((item) => (
        <li key={item.to}>
          <Link
            to={item.to}
            replace={replace}
            // Fechar no toque, e não numa reação à mudança de rota: o painel
            // some junto com o gesto, sem o quadro em que a tela nova já está
            // atrás de um menu ainda aberto.
            onClick={onSelect}
            className="bg-surface-subtle flex min-h-24 flex-col items-center justify-center gap-2 rounded-xl p-2 text-center active:opacity-80"
          >
            {/* Decorativo: o rótulo logo abaixo diz a mesma coisa. */}
            <Icon as={item.icon} size="xl" className="text-primary" />

            <Text variant="caption" as="span" className="leading-tight">
              {item.label}
            </Text>
          </Link>
        </li>
      ))}
    </ul>
  );
}
